<template>
    <div class="my-5">
        <div class="flex justify-between items-center mb-4">
            <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Planos de Recebimento</h1>
            <div class="flex space-x-2">
                <button
                    v-if="hasPermission('create.receivement_plans') || store.permissions"
                    class="inline-flex items-center justify-center rounded-md bg-indigo-500 px-2 py-1 text-sm font-semibold leading-6 text-white shadow transition duration-150 ease-in-out hover:bg-indigo-400"
                    @click="openModal()"
                >
                    <PlusIcon class="h-5 w-5 shrink-0 text-white-400 mr-1 h-3 w-3 shrink-0" aria-hidden="true" />
                    Novo Plano
                </button>
            </div>
        </div>

        <table-component
            :loading="loading"
            :pagination="pagination"
            @fetch="fetch"
            :headers="[
                'Ativo',
                'Nome',
                'Antecipação',
                'Taxa de Plataforma',
                'Taxa de Parcelamento',
                'Repasse',
                'Ações',
            ]"
        >
            <tr v-for="plan in plans" :key="plan.id">
                <td class="border-b border-slate-100 p-3 text-slate-500 dark:border-slate-700 dark:text-slate-200">
                    <span
                        :class="
                            plan.active
                                ? 'rounded bg-green-600 px-2 py-1 text-xs font-medium text-white'
                                : 'rounded bg-gray-400 px-2 py-1 text-xs font-medium text-white'
                        "
                    >
                        {{ plan.active ? "Ativo" : "Inativo" }}
                    </span>
                </td>
                <td class="border-b border-slate-100 p-3 text-slate-500 dark:border-slate-700 dark:text-slate-200">
                    <span class="font-medium text-gray-900 dark:text-white">
                        {{ plan.name }}
                    </span>
                </td>
                <td class="border-b border-slate-100 p-3 text-slate-500 dark:border-slate-700 dark:text-slate-200">
                    <div class="space-y-1 text-xs text-gray-700 dark:text-gray-300">
                        <div>
                            Dias de Antecipação: <span class="font-medium">{{ plan.anticipation_days || 'N/A' }}</span>
                        </div>
                        <div>
                            Taxa de Antecipação: <span class="font-medium text-blue-600 dark:text-blue-400">{{ formatPercentage(plan.anticipation_fee, 2) }}</span>
                        </div>
                        <div>
                            Limite: <span class="font-medium text-green-600 dark:text-green-400">{{ formatPercentage(plan.anticipation_limit, 2) }}</span>
                        </div>
                    </div>
                </td>
                <td class="border-b border-slate-100 p-3 text-slate-500 dark:border-slate-700 dark:text-slate-200">
                    <div class="space-y-1 text-xs text-gray-700 dark:text-gray-300">
                        <div>
                            Boleto: <span class="font-medium text-blue-600 dark:text-blue-400">{{ formatPercentage(plan.bank_slip?.platform_fee, 2) }}</span>
                        </div>
                        <div>
                            Cartão de Crédito: <span class="font-medium text-blue-600 dark:text-blue-400">{{ formatPercentage(plan.card?.platform_fee, 2) }}</span>
                        </div>
                        <div>
                            Pix: <span class="font-medium text-blue-600 dark:text-blue-400">{{ formatPercentage(plan.pix?.platform_fee, 2) }}</span>
                        </div>
                        <div v-if="plan.international?.platform_fee">
                            Internacional: <span class="font-medium text-purple-600 dark:text-purple-400">{{ formatPercentage(plan.international.platform_fee, 2) }}</span>
                        </div>
                    </div>
                </td>
                <td class="border-b border-slate-100 p-3 text-slate-500 dark:border-slate-700 dark:text-slate-200">
                    <div class="space-y-1 text-xs text-gray-700 dark:text-gray-300">
                        <div>
                            Boleto: <span class="font-medium text-green-600 dark:text-green-400">{{ formatPercentage(plan.bank_slip?.installment_fee, 2) }}</span>
                        </div>
                        <div>
                            Cartão de Crédito: <span class="font-medium text-green-600 dark:text-green-400">{{ formatPercentage(plan.card?.installment_fee, 2) }}</span>
                        </div>
                    </div>
                </td>
                <td class="border-b border-slate-100 p-3 text-slate-500 dark:border-slate-700 dark:text-slate-200">
                    <div class="space-y-1 text-xs text-gray-700 dark:text-gray-300">
                        <div>
                            Boleto: <span class="font-medium text-orange-600 dark:text-orange-400">{{ plan.bank_slip?.withdraw_time || 0 }} dias</span>
                        </div>
                        <div>
                            Cartão de Crédito: <span class="font-medium text-orange-600 dark:text-orange-400">{{ plan.card?.withdraw_time || 0 }} dias</span>
                        </div>
                        <div>
                            Pix: <span class="font-medium text-orange-600 dark:text-orange-400">{{ plan.pix?.withdraw_time || 0 }} dias</span>
                        </div>
                    </div>
                </td>
                <td class="border-b border-slate-100 p-3 text-slate-500 dark:border-slate-700 dark:text-slate-200">
                    <button
                        class="inline-flex items-center justify-center rounded-md bg-indigo-500 px-2 py-1 text-sm font-semibold leading-6 text-white shadow transition duration-150 ease-in-out hover:bg-indigo-400"
                        @click="openModal(plan)"
                    >
                        <PencilIcon class="h-5 w-5 shrink-0 text-white-400 mr-1 h-3 w-3 shrink-0" aria-hidden="true" /> Editar
                    </button>
                </td>
            </tr>
        </table-component>

        <plan-modal
            :open="showModal"
            :editing-plan="editingPlan"
            :loading="loading"
            @close="closeModal"
            @save="savePlan"
        />
    </div>
</template>

<script>
import TableComponent from "../../components/layouts/table/TableComponent.vue";
import PlanModal from "../../components/receivement/PlanModal.vue";
import permissionMixin from "../../mixins/PermissionMixin.vue";
import { Store } from "../../stores/store";
import { PlusIcon } from "@heroicons/vue/24/outline";
import { PencilIcon } from "@heroicons/vue/24/outline";

export default {
    name: "ReceivementPlans",
    mixins: [permissionMixin],
    components: {
        TableComponent,
        PlanModal,
        PlusIcon,
        PencilIcon,
    },
    data() {
        return {
            loading: false,
            plans: [],
            showModal: false,
            editingPlan: null,
            store: Store(),
            pagination: {
                total: 0,
                per_page: 15,
                current_page: 1,
                last_page: 1,
                from: 1,
                to: 1,
            },
        };
    },
    methods: {
        async fetchPlans() {
            await this.fetch();
        },

        async fetch() {
            try {
                this.loading = true;

                const params = new URLSearchParams({
                    page: this.pagination.current_page.toString(),
                    per_page: this.pagination.per_page.toString(),
                });

                const response = await fetch(`/api/receivement-plans?${params.toString()}`);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const result = await response.json();

                if (response.status >= 200 && response.status < 300) {
                    // A API retorna dados paginados no formato Laravel
                    this.plans = result.data.data || [];

                    // Atualiza a paginação com os dados retornados
                    this.pagination = {
                        ...this.pagination,
                        total: result.data.total || 0,
                        per_page: result.data.per_page || 15,
                        current_page: result.data.current_page || 1,
                        last_page: result.data.last_page || 1,
                        from: result.data.from || 1,
                        to: result.data.to || 1,
                    };
                } else {
                    console.error('Erro ao carregar planos:', result.message);
                }
            } catch (error) {
                console.error('Erro ao carregar planos:', error);
            } finally {
                this.loading = false;
            }
        },

        openModal(plan) {
            this.editingPlan = plan || null;
            this.showModal = true;
        },

        closeModal() {
            this.showModal = false;
            this.editingPlan = null;
        },

        async savePlan(formData) {
            try {
                this.loading = true;

                const url = this.editingPlan
                    ? `/api/receivement-plans/${this.editingPlan.id}`
                    : '/api/receivement-plans';


                const method = this.editingPlan ? 'PUT' : 'POST';

                const requestData = this.transformFormData(formData);

                const response = await fetch(url, {
                    method,
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                    },
                    body: JSON.stringify(requestData),
                });

                const responseText = await response.text();

                let data;
                try {
                    data = JSON.parse(responseText);
                } catch (parseError) {
                    console.error('Erro ao fazer parse da resposta:', parseError);
                    console.error('Resposta que falhou:', responseText);
                    throw new Error(`Resposta inválida do servidor: ${responseText}`);
                }

                if (response.status >= 200 && response.status < 300) {
                    // Recarrega os planos para obter dados atualizados
                    await this.fetch();
                    this.closeModal();
                } else {
                    console.error('Erro ao salvar plano:', data.message);
                    alert('Erro ao salvar plano: ' + data.message);
                }
            } catch (error) {
                console.error('Erro ao salvar plano:', error);
                alert('Erro ao salvar plano: ' + error.message);
            } finally {
                this.loading = false;
            }
        },

        transformFormData(formData) {
            // Converte dados do frontend para o formato da API
            const transformedData = {
                name: formData.name,
                active: formData.active ?? true,
                featured: formData.featured ?? false,
                fixed_fee: Math.round(parseFloat(formData.fixedFee || 0) * 100), // Converte reais para centavos
                fixed_fee_international: Math.round(parseFloat(formData.fixedFeeInternational || 0) * 100), // Converte reais para centavos
                receivement_time: parseInt(formData.receivementTime || 0),
                platform_fee: parseFloat(formData.platformFee || 0),
                anticipation_fee: parseFloat(formData.anticipationFee || 0),
                anticipation_days: formData.anticipationDays ? parseInt(formData.anticipationDays) : null,
                anticipation_limit: formData.anticipationLimit ? parseInt(formData.anticipationLimit) : null,
                platform_fees: formData.platformFees || null,
                installment_fees: formData.installmentFees || null,
                transfer_days: formData.transferDays || null,
                // Converte os objetos para arrays com as chaves corretas
                card: {
                    withdraw_time: parseInt(formData.card?.withdraw_time || 0),
                    platform_fee: parseFloat(formData.card?.platform_fee || 0),
                    installment_fee: parseFloat(formData.card?.installment_fee || 0),
                },
                bank_slip: {
                    withdraw_time: parseInt(formData.bank_slip?.withdraw_time || 0),
                    platform_fee: parseFloat(formData.bank_slip?.platform_fee || 0),
                    installment_fee: parseFloat(formData.bank_slip?.installment_fee || 0),
                },
                pix: {
                    withdraw_time: parseInt(formData.pix?.withdraw_time || 0),
                    platform_fee: parseFloat(formData.pix?.platform_fee || 0),
                    installment_fee: parseFloat(formData.pix?.installment_fee || 0),
                },
                pix_recurring: {
                    withdraw_time: parseInt(formData.pix_recurring?.withdraw_time || 0),
                    platform_fee: parseFloat(formData.pix_recurring?.platform_fee || 0),
                },
                international: {
                    withdraw_time: parseInt(formData.international?.withdraw_time || 0),
                    platform_fee: parseFloat(formData.international?.platform_fee || 0),
                },
            };

            return transformedData;
        },

        async togglePlanStatus(plan) {
            try {
                const response = await fetch(`/api/receivement-plans/${plan.id}/toggle-status`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                    },
                });

                const data = await response.json();

                if (response.status >= 200 && response.status < 300) {
                    // Atualiza o plano localmente
                    Object.assign(plan, data.data);
                } else {
                    console.error('Erro ao alterar status:', data.message);
                    alert('Erro ao alterar status: ' + data.message);
                }
            } catch (error) {
                console.error('Erro ao alterar status:', error);
                alert('Erro ao alterar status. Tente novamente.');
            }
        },

        async refreshPlans() {
            this.pagination.current_page = 1;
            await this.fetch();
        },

        formatPercentage(value, decimalPlaces) {
            if (value === null || value === undefined) {
                return 'N/A';
            }
            return `${parseFloat(value).toFixed(decimalPlaces)}%`;
        }
    },

    computed: {
        hasPlans() {
            return this.plans && this.plans.length > 0;
        },
    },

    watch: {
        // Watcher removido - não é mais necessário
    },

    async mounted() {
        await this.fetchPlans();

        // Carrega as permissões do usuário
        try {
            await this.store.fetchAuthPermissions();
        } catch (error) {
            console.error('Erro ao carregar permissões:', error);
        }
    },
};
</script>

<style scoped></style>
